import { Eye, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cancelWithdrawalPayout } from "../../api/modules/b2droppie.class";
import { useDispatch } from "react-redux";
import {
  paymentSuccess,
  setPaymentType,
} from "../../redux/slices/paymentSlice";
import { togglePayment } from "../../redux/slices/accountSlice";

const PendingWithdrawals = ({
  show,
  onHide,
  isDarkMode,
  setLoading,
  refreshCart,
  setCartToast,
  refreshPendingWithdrawals,
  pendingWithdrawalsData = [],
}) => {
  const dispatch = useDispatch();

  const startY = useRef(0);
  const modalRef = useRef(null);
  const [dragPosition, setDragPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCancelId, setSelectedCancelId] = useState(null);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
      setDragPosition(0);
    } else {
      document.body.style.overflow = "auto";
      setShowCancelConfirm(false);
      setSelectedCancelId(null);
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0) setDragPosition(diff);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragPosition > 100) {
      onHide?.();
      setTimeout(() => setDragPosition(0), 300);
    } else {
      setDragPosition(0);
    }
  };

  const handleCancelClick = (breeze_id) => {
    if (!breeze_id) return;

    setSelectedCancelId(breeze_id);
    setShowCancelConfirm(true);
  };

  const confirmCancel = async () => {
    if (!selectedCancelId) return;

    setShowCancelConfirm(false);

    await handleCancel(selectedCancelId);

    setSelectedCancelId(null);
  };

  const handleCancel = async (breeze_id) => {
    if (!breeze_id) return;

    setLoading(true);
    try {
      const res = await cancelWithdrawalPayout(breeze_id);
      if (res?.data?.success) {
        setCartToast({
          success: res?.data?.message || "Withdrawal cancelled successfully!",
        });

        setTimeout(() => {
          refreshCart?.();
          refreshPendingWithdrawals?.();
          onHide?.();
        }, 1000);
      } else {
        setCartToast({ error: res?.data?.message || "Cancel failed" });
      }
    } catch (err) {
      setCartToast({ error: "Cancel failed" });
    } finally {
      setLoading(false);
    }
  };

  const handlePayout = (withdrawal) => {
    if (!withdrawal?.payout_page) return;

    dispatch(
      paymentSuccess({
        ...withdrawal,
        url: withdrawal.payout_page,
      }),
    );

    dispatch(setPaymentType("withdraw"));
    dispatch(togglePayment());

    refreshCart?.();
    refreshPendingWithdrawals?.();
    onHide?.();
  };

  const statusBadge = (status) => {
    const normalized = status?.toString()?.trim()?.toUpperCase();

    return (
      <span className={`custom-status-badge ${normalized?.toLowerCase()}`}>
        {normalized}
      </span>
    );
  };

  const handleViewProducts = (products = []) => {
    setSelectedProducts(products);
    setShowProductsModal(true);
  };

  if (!show) return null;

  return (
    <>
      <div
        className={`modal-overlay ${isDarkMode ? "dark" : "light"}`}
        onClick={(e) => {
          if (e.target.className === "modal-overlay") {
            onHide?.();
          }
        }}
      >
        <div
          ref={modalRef}
          className={`modal-container ${isDarkMode ? "modal-dark" : ""}`}
          style={{
            transform: `translateY(${dragPosition}px)`,
            transition: isDragging ? "none" : "transform 0.3s ease",
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Drag Handle */}
          <div className="modal-drag-handle">
            <div className="drag-bar"></div>
          </div>

          {/* Header */}
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Pending Withdrawals</h5>

            <button className="modal-close-btn" onClick={onHide}>
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body-custom">
            {pendingWithdrawalsData?.length > 0 ? (
              <>
                <div className="desktop-table-view">
                  <table
                    className={`products-table ${
                      isDarkMode ? "products-table-dark" : ""
                    }`}
                  >
                    <thead>
                      <tr>
                        <th className="text-center">ID</th>
                        <th className="text-center">Amount</th>
                        <th>Status</th>
                        <th style={{ textAlign: "center" }}>Actions</th>
                        <th className="text-end">View</th>
                      </tr>
                    </thead>

                    <tbody>
                      {pendingWithdrawalsData.map((withdrawal, index) => (
                        <tr key={index}>
                          <td>
                            <span className="product-name mt-0">
                              {index + 1}
                            </span>
                          </td>

                          <td className="price-cell text-center">
                            ${withdrawal?.payment_amount}
                          </td>

                          <td className="price-cell text-start">
                            {statusBadge(withdrawal?.status)}
                          </td>

                          <td style={{ textAlign: "center" }}>
                            <div className="pw-actions">
                              <button
                                className="deep-primary-monochrome-btn-dark"
                                onClick={() =>
                                  handleCancelClick(withdrawal?.breeze_page_id)
                                }
                              >
                                Cancel
                              </button>

                              <button
                                className="filled-medium-deep-yellow-btn-dark"
                                onClick={() => handlePayout(withdrawal)}
                              >
                                Payout
                              </button>
                            </div>
                          </td>
                          <td className="tablehead text-end">
                            {withdrawal?.products?.length > 0 ? (
                              <button
                                className="eye-button ms-auto"
                                onClick={() =>
                                  handleViewProducts(withdrawal.products)
                                }
                              >
                                <Eye
                                  size={20}
                                  color={isDarkMode ? "#fff" : "#684199"}
                                />
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile View */}
                <div className="mobile-cards-view">
                  {/* Header */}
                  {/* <div
                    className="mobile-header-row"
                    style={{
                      display: "flex",
                      gridTemplateColumns: "1.5fr 2fr 1fr",
                      padding: "8px 12px",
                      fontSize: "12px",
                      color: "#9ca3af",
                    }}
                  >
                    <span>ID</span>
                    <span>Amount</span>
                    <span>Status</span>
                    <span>Actions</span>
                    <span style={{ textAlign: "right" }}>View</span>
                  </div> */}
                  <table
                    className={`products-table ${
                      isDarkMode ? "products-table-dark" : ""
                    }`}
                  >
                    <thead>
                      <tr>
                        <th className="text-center">ID</th>
                        <th className="text-center">Amount</th>
                        <th>Status</th>
                        <th style={{ textAlign: "center" }}>Actions</th>
                        <th className="text-end">View</th>
                      </tr>
                    </thead>

                    <tbody>
                      {pendingWithdrawalsData.map((withdrawal, index) => (
                        <tr key={index}>
                          <td>
                            <span className="product-name mt-0">
                              {index + 1}
                            </span>
                          </td>

                          <td className="price-cell text-center">
                            ${withdrawal?.payment_amount}
                          </td>

                          <td className="price-cell text-start">
                            {statusBadge(withdrawal?.status)}
                          </td>

                          <td style={{ textAlign: "center" }}>
                            <div className="pw-actions">
                              <button
                                className="deep-primary-monochrome-btn-dark"
                                onClick={() =>
                                  handleCancelClick(withdrawal?.breeze_page_id)
                                }
                              >
                                Cancel
                              </button>

                              <button
                                className="filled-medium-deep-yellow-btn-dark"
                                onClick={() => handlePayout(withdrawal)}
                              >
                                Payout
                              </button>
                            </div>
                          </td>
                          <td
                            className="tablehead text-end"
                          >
                            {withdrawal?.products?.length > 0 ? (
                              <button
                                className="eye-button ms-auto"
                                onClick={() =>
                                  handleViewProducts(withdrawal.products)
                                }
                              >
                                <Eye
                                  size={20}
                                  color={isDarkMode ? "#fff" : "#684199"}
                                />
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="no-products-text">No pending withdrawals found.</p>
            )}
          </div>
        </div>
      </div>

      {showCancelConfirm && (
        <div
          className={`confirm-overlay ${isDarkMode ? "dark" : "light"}`}
          onClick={() => {
            setShowCancelConfirm(false);
            setSelectedCancelId(null);
          }}
        >
          <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="d-center">
              <h2>Cancel Withdrawal?</h2>
            </div>

            <p className="text-center mt-2 mb-4">
              Are you sure you want to cancel this withdrawal?
            </p>

            <div className="confirm-actions">
              <button
                className="confirm-btn cancel"
                onClick={() => {
                  setShowCancelConfirm(false);
                  setSelectedCancelId(null);
                }}
              >
                No
              </button>

              <button className="confirm-btn yes" onClick={confirmCancel}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {showProductsModal && (
        <div
          className={`modal-overlay ${isDarkMode ? "dark" : "light"}`}
          style={{
            zIndex: 11,
            background: "rgba(0,0,0,0.6)",
          }}
          onClick={() => setShowProductsModal(false)}
        >
          <div
            className={`modal-container ${isDarkMode ? "modal-dark" : ""}`}
            style={{
              maxWidth: "900px",
              transform: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="modal-header-custom">
              <h5 className="modal-title-custom">Withdrawal Products</h5>
              <button
                className="modal-close-btn"
                onClick={() => setShowProductsModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            {/* Body */}
            <div className="modal-body-custom">
              <table
                className={`products-table ${
                  isDarkMode ? "products-table-dark" : ""
                }`}
              >
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th className="text-center">Price</th>
                    <th className="text-center">Qty</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {selectedProducts.map((product) => (
                    <tr key={product.id}>
                      <td>
                        <img
                          src={product.image}
                          alt={product.name}
                          width={50}
                          height={50}
                          style={{
                            borderRadius: 10,
                            objectFit: "cover",
                          }}
                        />
                      </td>

                      <td>{product.name}</td>

                      <td className="text-center">{product.formatted_price}</td>

                      <td className="text-center">
                        {product?.pivot?.quantity ?? 1}
                      </td>

                      <td className="text-center">
                        <span
                          className={`custom-status-badge ${product.tpp_status}`}
                        >
                          {product.tpp_status?.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {selectedProducts.length === 0 && (
                <p className="text-center mt-3">No products found.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PendingWithdrawals;
